"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Clock, FlaskConical, CircleQuestionMark as HelpCircle, Loader as Loader2, Mail, MapPin, MessageSquare, PhoneCall, Send, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const keyBenefits = [{
  icon: FlaskConical,
  title: "Chromatography Specialist Support",
  description: "Direct technical consultation for method development, column screening, and HPLC optimization."
}, {
  icon: Clock,
  title: "Feasibility Studies",
  description: "Evaluation of sample runs and solvent recovery workflows to assess feasibility."
}, {
  icon: ShieldCheck,
  title: "Qualification & Lifecycle Support",
  description: "Documentation and support for instrument qualification and ongoing maintenance."
}];
function InquiryFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-secondary/60 border-border rounded-xl border p-8 text-center">
        <div className="bg-primary/10 mx-auto mb-4 flex size-12 items-center justify-center rounded-full">
          <CheckCircle2 className="text-primary size-6" />
        </div>
        <h3 className="text-foreground text-xl font-semibold">
          Inquiry Received
        </h3>
        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
          {successMessage || "Thank you for contacting Antech Solutions. Our analytical specialists will review your application requirements and respond within 24 hours."}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button onClick={resetForm} variant="outline">
            Submit Another Request
          </Button>
          <Button asChild>
            <Link to="/DiscussYourApplicationPage">
              Explore Consultation Options
            </Link>
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Please enter your full name"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name *</FormLabel>
              <FormControl>
                <Input placeholder="Dr. Sarah Jenkins" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid work email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Work Email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="s.jenkins@biotech.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="organization" rules={{
        required: "Company or laboratory name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Laboratory / Organization *</FormLabel>
              <FormControl>
                <Input placeholder="Pharma Solutions Ireland" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="applicationFocus" rules={{
        required: "Please select an analytical area"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Application Area *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select application domain" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="hplc_uhplc">
                    HPLC / UHPLC Method Development
                  </SelectItem>
                  <SelectItem value="solvent_recovery">
                    Solvent Recovery (ReSolve)
                  </SelectItem>
                  <SelectItem value="pat_inline">
                    Process Analytical Technology (PAT)
                  </SelectItem>
                  <SelectItem value="raw_material_id">
                    Raw Material Identification (Raman/NIR)
                  </SelectItem>
                  <SelectItem value="powder_tablet">
                    Powder &amp; Tablet Physical Testing
                  </SelectItem>
                  <SelectItem value="qualification_service">
                    Validation &amp; Lifecycle Maintenance
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="projectDetails" rules={{
      required: "Please describe your analytical challenge or inquiry",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Application Requirements &amp; Target Matrix *</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your current analytical challenges, sample matrices, throughput, or target compounds..." className="min-h-28" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border-destructive/30 bg-destructive/10 text-destructive rounded-md border p-3 text-sm">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full shadow-xs">
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Transmitting Inquiry...
            </> : <>
              <Send className="mr-2 size-4" />
              Submit Application Inquiry
            </>}
        </Button>
      </div>
      <p className="text-muted-foreground text-center text-xs">
        Your data is treated with confidentiality. We comply with industry
        standards for regulated pharmaceutical and analytical data.
      </p>
    </Form>;
}
export default function ApplicationInquiry() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="applicationinquirycta" data-nav="light" className="bg-background relative overflow-hidden py-16 md:py-24">
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12">
          {/* Left Column: Context & Direct Value */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex flex-col gap-6 lg:col-span-5">
            <div>
              <Badge variant="outline" className="mb-3 font-medium">
                Application-Led Engineering
              </Badge>
              <h2 className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
                Discuss Your Chromatography Needs
              </h2>
              <p className="text-muted-foreground mt-4 text-base leading-relaxed">
                Whether you are refining complex HPLC methods, implementing
                solvent recovery systems, or qualifying process analytical tools,
                our application team provides practical support across Ireland
                and the UK.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              {keyBenefits.map((benefit, i) => {
              const IconComponent = benefit.icon;
              return <div key={benefit.title} data-index={i} className="border-border/60 bg-card flex items-start gap-4 rounded-lg border p-4 shadow-2xs">
                    <div className="bg-primary/10 text-primary mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md">
                      <IconComponent className="size-5" />
                    </div>
                    <div>
                      <h3 className="text-foreground text-sm font-semibold">
                        {benefit.title}
                      </h3>
                      <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <div className="bg-secondary/40 border-border/80 rounded-xl border p-5">
              <h4 className="text-foreground flex items-center gap-2 text-sm font-semibold">
                <HelpCircle className="text-primary size-4" />
                Need an urgent technical consultation?
              </h4>
              <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                Connect directly with our dedicated technical team for
                immediate qualification or repair requests.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/SupportRequestPage">
                    <PhoneCall className="mr-1.5 size-3.5" />
                    Support Request
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/BookADemoPage">
                    <FlaskConical className="mr-1.5 size-3.5" />
                    Book Laboratory Demo
                  </Link>
                </Button>
              </div>
            </div>

            <div className="text-muted-foreground flex flex-wrap items-center gap-6 text-xs">
              <div className="flex items-center gap-1.5">
                <MapPin className="text-primary size-3.5" />
                <span>Ireland &amp; UK Coverage</span>
              </div>
              <div className="flex items-center gap-1.5">
                <FlaskConical className="text-primary size-3.5" />
                <span>Technical Application Support</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Inquiry Form */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 24
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <Card className="border-border shadow-md">
              <CardHeader className="border-b pb-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl">
                      Application Inquiry Form
                    </CardTitle>
                    <CardDescription className="mt-1 text-xs sm:text-sm">
                      Provide details regarding your target assay, compound, or
                      process constraints.
                    </CardDescription>
                  </div>
                  <div className="bg-primary/10 text-primary hidden size-10 shrink-0 items-center justify-center rounded-full sm:flex">
                    <MessageSquare className="size-5" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="chromatography-application-inquiry" sectionName="applicationinquirycta" defaultValues={{
                fullName: "",
                email: "",
                organization: "",
                applicationFocus: "",
                projectDetails: ""
              }} validationRules={{
                fullName: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                organization: {
                  required: true
                },
                applicationFocus: {
                  required: true
                },
                projectDetails: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Submit Application Inquiry" successMessage="Thank you! Your application inquiry has been routed to our chromatography technical specialists.">
                  <InquiryFormFields />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}